import { fetchById, fetchByPostalCode, } from '@price-pages-agency-cards/src/application/agency.queries';
import { logger } from '@price-pages-agency-cards/src/infrastructure/logger';
import { create } from 'zustand';
const initialState = {
    data: {},
    error: undefined,
    isFetchAgenciesByIdsLoading: false,
    isFetchAgencyByIdLoading: false,
    isFetchRecommendedAgenciesLoading: false,
};
export const createAgencyStore = (gateway) => create((set, get) => ({
    ...initialState,
    fetchAgenciesByIds: async ({ ids, brand, locale, }) => {
        const idsToFetch = ids.filter((agencyId) => !get().getAgencyById(agencyId));
        if (!idsToFetch.length)
            return;
        set((state) => ({ ...state, error: undefined, isFetchAgenciesByIdsLoading: true }));
        const promises = idsToFetch.map((id) => fetchById(gateway, id, brand, locale));
        try {
            const agencies = await Promise.all(promises);
            const agenciesToStore = agencies.reduce((acc, agency) => {
                if (!agency)
                    return acc;
                return {
                    ...acc,
                    [agency.id]: agency,
                };
            }, {});
            set((state) => ({
                ...state,
                data: {
                    ...state.data,
                    ...agenciesToStore,
                },
                isFetchAgenciesByIdsLoading: false,
            }));
        }
        catch (error) {
            logger.error('Error in fetchAgenciesByIds', error);
            set((state) => ({
                ...state,
                error: error,
                isFetchAgenciesByIdsLoading: false,
            }));
        }
    },
    fetchAgencyById: async ({ id, brand, locale, }) => {
        if (get().getAgencyById(id))
            return;
        set((state) => ({ ...state, error: undefined, isFetchAgencyByIdLoading: true }));
        try {
            const agency = await fetchById(gateway, id, brand, locale);
            set((state) => ({
                ...state,
                data: agency ? { ...state.data, [id]: agency } : state.data,
                isFetchAgencyByIdLoading: false,
            }));
        }
        catch (error) {
            logger.error('Error in fetchAgencyById', error);
            set((state) => ({
                ...state,
                error: error,
                isFetchAgencyByIdLoading: false,
            }));
        }
    },
    fetchRecommendedAgencies: async ({ postalCode, brand, }) => {
        set((state) => ({
            ...state,
            error: undefined,
            isFetchRecommendedAgenciesLoading: true,
        }));
        get().flushRecommendedAgencies();
        try {
            const agencies = await fetchByPostalCode(gateway, postalCode, brand);
            const agenciesToStore = agencies.reduce((acc, agency) => ({
                ...acc,
                [agency.id]: agency,
            }), {});
            set((state) => ({
                ...state,
                data: {
                    ...state.data,
                    ...agenciesToStore,
                },
                isFetchRecommendedAgenciesLoading: false,
            }));
        }
        catch (error) {
            logger.error('Error in fetchRecommendedAgencies', error);
            set((state) => ({
                ...state,
                error: error,
                isFetchRecommendedAgenciesLoading: false,
            }));
        }
    },
    flush: () => {
        set(initialState);
    },
    flushAgency: (id) => {
        set((state) => {
            const { [id]: _, ...remainingData } = state.data;
            return {
                ...state,
                data: remainingData,
            };
        });
    },
    flushRecommendedAgencies: () => {
        set((state) => {
            const updatedData = Object.entries(state.data).reduce((acc, [id, agency]) => {
                if (agency.isRecommended) {
                    return acc;
                }
                return { ...acc, [id]: agency };
            }, {});
            return {
                ...state,
                data: updatedData,
            };
        });
    },
    getAgenciesByIds: (ids) => {
        const state = get();
        return ids
            .map((id) => state.data[id])
            .filter((agency) => agency !== undefined);
    },
    getAgencyById: (id) => {
        return get().data[id];
    },
    getRecommendedAgencies: () => {
        const state = get();
        return Object.values(state.data).filter((agency) => agency.isRecommended);
    },
    lastZipCode: '',
    setLastZipCode: (lastUserSearch) => {
        set((state) => ({ ...state, lastZipCode: lastUserSearch }));
    },
}));
